import React, { useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Modal, Stack, TextField, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { useActionHook, useModals } from "hooks";
import { IApiServiceError, useUserActions } from "store";
import { getDomain } from "utils/auth";

interface Props {
  isOpen: boolean;
}

const LOCALHOST = "localhost";
const apiDomain = getDomain();

export const UpgradeAccount: React.FC<Props> = ({ isOpen }) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");

  const { closeUpgradeAccount } = useModals();
  const { upgradeAccountAction } = useUserActions();

  const redirectToLogin = (domain: string) => {
    if (location.hostname === LOCALHOST) {
      return console.log(
        "If you hadn't been at localhost you would be redirected to log in to a newly created account."
      );
    }
    const link = document.createElement("a");
    link.href = `${import.meta.env.VITE_PROTOCOL}${domain}`;

    link.click();
  };

  const { loading, action } = useActionHook(upgradeAccountAction, {
    onSuccess: () => redirectToLogin(`${inputValue}${postfix}`),
    onReject: (error: IApiServiceError) => {
      const domainError = error.errors?.["domain"]?.[0];
      if (domainError) {
        setValidationError(domainError);
      }
    }
  });

  const postfix: string = useMemo(
    () =>
      "." +
      apiDomain
        .split(".")
        .filter((_, index, array) => Boolean(index === array.length - 1 || index == array.length - 2))
        .join("."),
    []
  );

  const handleSubmit = useCallback(() => {
    if (loading) return;
    if (!inputValue) return;
    action({ domain: `${inputValue}${postfix}` });
  }, [inputValue, loading]);

  const handleClosePopup = useCallback(() => {
    closeUpgradeAccount();
    setInputValue("");
    setValidationError("");
  }, []);

  return (
    <Modal open={isOpen} onClose={handleClosePopup}>
      <ProgressOverlay
        containerSX={{
          ...style,
          display: "flex",
          flexDirection: "column",
          width: 520,
          p: "32px",
          borderRadius: "8px",
          bgcolor: "white",
          gap: "24px",
          boxShadow:
            "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
        }}
        inProgress={loading}
      >
        <Stack direction="row" sx={{ justifyContent: "space-between", borderBottom: "1px solid #DBE8F5", pb: "6px" }}>
          <Typography sx={{ fontSize: "18px", lineHeight: "24px", color: "#071F35", fontWeight: 400 }}>
            Upgrade account
          </Typography>
          <IconButton size="sm" onClick={handleClosePopup}>
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box sx={{ position: "relative" }}>
          <TextField
            label="Company Domain"
            placeholder="Domain"
            value={inputValue}
            onChange={(e) => {
              const { value } = e.target;

              // Limit with 25 symbols
              if (value.length >= 35) return;

              // Don't start with "-"
              if (/^-/.test(value)) return;

              // Let user enter only small letters and numbers
              if (!/^[a-z,0-9,-]*$/.test(value)) return;

              if (validationError) {
                setValidationError("");
              }
              setInputValue(value);
            }}
            sx={{ width: "100%", zIndex: 1 }}
          />
          <TextField
            sx={{
              width: "100%",
              position: "absolute",
              left: 0,
              zIndex: 0
            }}
            disabled
            value={inputValue ? `${inputValue}${postfix}` : ""}
            label="Company Domain"
            helperText={validationError}
            error={Boolean(validationError)}
          />
        </Box>
        <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "8px" }}>
          <Button onClick={handleClosePopup} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={inputValue.length < 2 || Boolean(validationError)}
            onClick={handleSubmit}
          >
            Apply
          </Button>
        </Stack>
      </ProgressOverlay>
    </Modal>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
